import * as React from 'react';
import { isFunction } from '@aws-amplify/ui';
import { getInput } from '../../utils/getInput.mjs';
import { uploadFile } from '../../utils/uploadFile.mjs';
import { FileStatus } from '../../types.mjs';

function useUploadFiles({ accessLevel, files, isResumable, maxFileCount, onProcessFileSuccess, onUploadError, onUploadStart, onUploadSuccess, path, processFile, setUploadingFile, setUploadProgress, setUploadSuccess, }) {
    React.useEffect(() => {
        const filesReadyToUpload = files.filter((file) => file.status === FileStatus.QUEUED);
        if (filesReadyToUpload.length > maxFileCount) {
            return;
        }
        for (const { file, key, id } of filesReadyToUpload) {
            const onProgress = (event) => {
                /**
                 * When a file is zero bytes, the progress.total will equal zero.
                 * Therefore, this will prevent a divide by zero error.
                 */
                const progress = event.totalBytes === undefined || event.totalBytes === 0
                    ? 100
                    : Math.floor((event.transferredBytes / event.totalBytes) * 100);
                setUploadProgress({ id, progress });
            };
            if (file) {
                const handleProcessFileSuccess = (input) => onProcessFileSuccess({ id, ...input });
                const input = getInput({
                    accessLevel,
                    file,
                    key,
                    onProcessFileSuccess: handleProcessFileSuccess,
                    onProgress,
                    path,
                    processFile,
                });
                uploadFile({
                    input,
                    onComplete: (event) => {
                        if (isFunction(onUploadSuccess)) {
                            onUploadSuccess({
                                key: event.key ??
                                    event.path,
                            });
                        }
                        setUploadSuccess({ id });
                    },
                    onError: ({ key, error }) => {
                        if (isFunction(onUploadError)) {
                            onUploadError(error.message, { key });
                        }
                    },
                    onStart: ({ key, uploadTask }) => {
                        if (isFunction(onUploadStart)) {
                            onUploadStart({ key });
                        }
                        setUploadingFile({ id, uploadTask });
                    },
                });
            }
        }
    }, [
        files,
        accessLevel,
        isResumable,
        setUploadProgress,
        setUploadingFile,
        onUploadError,
        onProcessFileSuccess,
        onUploadSuccess,
        onUploadStart,
        maxFileCount,
        setUploadSuccess,
        processFile,
        path,
    ]);
}

export { useUploadFiles };
